import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ContainerSasUrl } from '../../interfaces/company';
import { ConnectionLogo } from '../../interfaces/common';

interface ProfileImageResponse {
  profile_image_url: string;
  expires_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class ImageService {
  private profileImageUrlSubject = new BehaviorSubject<string | null>(null);
  public profileImageUrl$ = this.profileImageUrlSubject.asObservable();

  public connectionImageUrlSubject = new BehaviorSubject<string | null>(null);
  public connectionImageUrl$ = this.connectionImageUrlSubject.asObservable();

  private http = inject(HttpClient);
  private apiBaseUrl = environment.apiBaseUrl;
  private containerSasUrlCache$?: Observable<ContainerSasUrl>;

  initialize(skipCdfiLogo = false) {
    this.GetProfileImage();
    if (!skipCdfiLogo) {
      this.GetConnectionImage();
    }
  }

  GetProfileImage(showLoader = false) {
    this.http
      .get<ProfileImageResponse>(`${this.apiBaseUrl}/api/profile/image`, {
        headers: { 'X-Skip-Loader': showLoader ? 'false' : 'true' },
      })
      .subscribe({
        next: (response) => {
          if (response && response.profile_image_url) {
            this.downloadAndCacheImage(
              response.profile_image_url,
              this.profileImageUrlSubject,
            );
          } else {
            this.profileImageUrlSubject.next(null);
          }
        },
        error: (err) => {
          console.error('Failed to fetch profile image SAS URL', err);
          this.profileImageUrlSubject.next(null);
        },
      });
  }

  GetConnectionImage(showLoader = false) {
    this.http
      .get<{ cdfi_logo_url: string; expires_at: string }>(
        `${this.apiBaseUrl}/api/cdfi/logo`,
        {
          headers: { 'X-Skip-Loader': showLoader ? 'false' : 'true' },
        },
      )
      .subscribe({
        next: (response) => {
          if (response && response.cdfi_logo_url) {
            this.downloadAndCacheImage(
              response.cdfi_logo_url,
              this.connectionImageUrlSubject,
            );
          } else {
            this.connectionImageUrlSubject.next(null);
          }
        },
        error: (err) => {
          console.error('Failed to fetch connection image SAS URL', err);
          this.connectionImageUrlSubject.next(null);
        },
      });
  }

  private downloadAndCacheImage(
    url: string,
    subject: BehaviorSubject<string | null>,
  ) {
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          subject.next(reader.result as string);
        };
        reader.readAsDataURL(blob);
      },
      error: (err) => {
        console.error('Failed to download image binary', err);
        subject.next(null);
      },
    });
  }

  uploadProfileImage(file: File) {
    const formData = new FormData();
    formData.append('image', file);

    return this.http
      .post(`${this.apiBaseUrl}/api/profile/upload-image`, formData)
      .pipe(
        tap(() => {
          this.GetProfileImage(true);
        }),
      );
  }

  uploadConnectionImage(file: File) {
    const formData = new FormData();
    formData.append('image', file);

    return this.http
      .post(`${this.apiBaseUrl}/api/cdfi/upload-logo`, formData)
      .pipe(
        tap(() => {
          this.GetConnectionImage(true);
        }),
      );
  }

  deleteProfileImage() {
    this.http.delete(`${this.apiBaseUrl}/api/profile/upload-image`).subscribe({
      next: () => {
        this.profileImageUrlSubject.next(null);
      },
      error: (err) => {
        console.error('Failed to delete profile image', err);
      },
    });
  }

  deleteConnectionImage() {
    this.http.delete(`${this.apiBaseUrl}/api/cdfi/upload-logo`).subscribe({
      next: () => {
        this.connectionImageUrlSubject.next(null);
      },
      error: (err) => {
        console.error('Failed to delete connection image', err);
      },
    });
  }

  clear() {
    this.profileImageUrlSubject.next(null);
    this.connectionImageUrlSubject.next(null);
    this.containerSasUrlCache$ = undefined;
  }

  getContainerSasUrl(): Observable<ContainerSasUrl> {
    // Return cached observable if available
    if (!this.containerSasUrlCache$) {
      this.containerSasUrlCache$ = this.http
        .get<ContainerSasUrl>(`${this.apiBaseUrl}/api/container/sas-url`)
        .pipe(
          shareReplay(1), // Cache the result and replay to late subscribers
        );
    }
    return this.containerSasUrlCache$;
  }

  checkImageExists(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => resolve(true);

      img.onerror = () => resolve(false);

      img.src = url;
    });
  }

  uploadConnectionLogoByAdmin(
    connectionId: string,
    file: File,
  ): Observable<ConnectionLogo> {
    const formData = new FormData();
    formData.append('image', file);

    return this.http.post<ConnectionLogo>(
      `${this.apiBaseUrl}/api/admin/${connectionId}/upload-logo`,
      formData,
    );
  }

  getConnectionLogo(connectionId: string): Observable<ConnectionLogo> {
    return this.http.get<ConnectionLogo>(
      `${this.apiBaseUrl}/api/admin/${connectionId}/upload-logo`,
    );
  }

  deleteConnectionLogo(connectionId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiBaseUrl}/api/admin/${connectionId}/upload-logo`,
    );
  }
}
